// ============================================
// DISTRIBUTION VIEW - Score distribution scatter
// ============================================

import { useMemo } from 'react';
import {
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import type { Employee } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import type { DemographicDimension } from '../../constants/demographics';
import { groupEmployeesByDemographic, getSegmentLabel, getSegmentColor } from '../../services/demographics';

interface DistributionViewProps {
    employees: Employee[];
    demographicDimension?: DemographicDimension | null;
}

export default function DistributionView({ employees, demographicDimension }: DistributionViewProps) {
    if (employees.length === 0) {
        return (
            <div className="analytics-empty-state">
                <p>אין נתונים להצגה</p>
            </div>
        );
    }

    // Get all categories from the first employee
    const categories = Object.keys(employees[0].scores);

    const demographicGroups = useMemo(() => {
        if (!demographicDimension) return null;
        return groupEmployeesByDemographic(employees, demographicDimension);
    }, [employees, demographicDimension]);

    // Build scatter data per group or all together
    const scatterGroups = useMemo(() => {
        if (!demographicGroups) {
            // No demographic grouping - single scatter
            const data: any[] = [];
            categories.forEach((category) => {
                employees.forEach(e => {
                    data.push({
                        muscle: getHebrewName(category),
                        score: e.scores[category] ?? 0,
                        name: e.name,
                    });
                });
            });
            return [{ key: 'all', label: 'משתתפים', color: '#133437', data }];
        }

        // Group by demographic
        const groups: { key: string; label: string; color: string; data: any[] }[] = [];
        demographicGroups.forEach((emps, segKey) => {
            const data: any[] = [];
            categories.forEach((category) => {
                emps.forEach(e => {
                    data.push({
                        muscle: getHebrewName(category),
                        score: e.scores[category] ?? 0,
                        name: e.name,
                    });
                });
            });
            groups.push({
                key: segKey,
                label: getSegmentLabel(segKey, demographicDimension!),
                color: getSegmentColor(segKey),
                data,
            });
        });
        return groups;
    }, [employees, categories, demographicGroups, demographicDimension]);

    return (
        <div className="analytics-distribution-container" style={{ direction: 'ltr' }}>
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid />
                    <XAxis
                        type="category"
                        dataKey="muscle"
                        allowDuplicatedCategory={false}
                        interval={0}
                        tick={{ fontSize: 10 }}
                    />
                    <YAxis
                        type="number"
                        dataKey="score"
                        domain={[0, 10]}
                        ticks={[0, 2, 4, 6, 8, 10]}
                    />
                    <Tooltip
                        cursor={{ strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                    <div style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #ebe6de',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                        direction: 'rtl',
                                        textAlign: 'right',
                                        padding: '12px',
                                        fontSize: '13px'
                                    }}>
                                        <p style={{ fontWeight: 800, marginBottom: '4px', borderBottom: '1px solid #f4f1eb', paddingBottom: '4px' }}>{d.name}</p>
                                        <div style={{ color: '#938977' }}>{d.muscle}: <b style={{ color: '#133437' }}>{d.score.toFixed(2)}</b></div>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    {demographicDimension && (
                        <Legend
                            content={() => (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '20px',
                                    direction: 'rtl',
                                    paddingTop: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    {scatterGroups.map(group => (
                                        <div key={group.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '50%',
                                                backgroundColor: group.color,
                                                display: 'inline-block',
                                                flexShrink: 0
                                            }} />
                                            <span style={{ fontSize: '12px', color: '#5c5549', whiteSpace: 'nowrap' }}>{group.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    )}
                    {scatterGroups.map(group => (
                        <Scatter
                            key={group.key}
                            name={group.label}
                            data={group.data}
                            fill={group.color}
                            opacity={0.6}
                        />
                    ))}
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
}
